// What? A component to display organization information and actions.
// Why? To separate organization display logic from the main dashboard.
// How? Renders different states: no org, pending invitations, pending access request, or has org.

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  Alert,
} from "@mui/joy";
import { Link } from "react-router-dom";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { User as UserType } from "../../common/types";
import PendingInvitationsCard from "./PendingInvitationsCard";
import { Invitation } from "../../common/types";
import Loading from "../Loading";

interface AvailableOrganization {
  organization_id: string;
  organization_name: string;
  verified_domains?: string[];
}

interface OrganizationCardProps {
  user: UserType;
  hasOrganization: boolean;
  hasPendingAccessRequest: boolean;
  isAdmin: boolean;
  orgStatus?: string;
  organizationName?: string | null; // Organization name fetched separately (for admins)
  loadingOrganizationDetails?: boolean; // Loading state for organization details
  pendingInvitations: Invitation[];
  loadingInvitations: boolean;
  organizationNames: Record<string, string>;
  invitationError: Record<string, string>;
  processingInvitation: string | null;
  availableOrganizations: AvailableOrganization[];
  onAcceptInvitation: (invitation: Invitation) => void;
  onOpenRequestAccessModal: (org: AvailableOrganization) => void;
  onCreateOrganization: () => void;
  getDaysUntilExpiration: (expiresAt: string) => number;
  isInvitationExpired: (expiresAt: string) => boolean;
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({
  user,
  hasOrganization,
  hasPendingAccessRequest,
  isAdmin,
  orgStatus,
  organizationName,
  loadingOrganizationDetails = false,
  pendingInvitations,
  loadingInvitations,
  organizationNames,
  invitationError,
  processingInvitation,
  availableOrganizations,
  onAcceptInvitation,
  onOpenRequestAccessModal,
  onCreateOrganization,
  getDaysUntilExpiration,
  isInvitationExpired,
}) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography level="h3" sx={{ mb: 3 }}>
          Organization
        </Typography>

        {hasPendingAccessRequest ? (
          // Pending access request - show pending message (check this FIRST)
          <Card
            variant="outlined"
            sx={{
              bgcolor: "background.level1",
              borderColor: "neutral.200",
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Typography level="body-md" sx={{ fontWeight: 600 }}>
                  Your request to join{" "}
                  <strong>{organizationName || "the organization"}</strong> is
                  pending approval from an administrator.
                </Typography>
                {user.access_request_message && (
                  <Typography level="body-sm" color="neutral">
                    Your message: {user.access_request_message}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        ) : !hasOrganization ? (
          // No organization - show invitations if any, otherwise show create button
          pendingInvitations.length > 0 ? (
            // Show pending invitations in Organization card
            <PendingInvitationsCard
              invitations={pendingInvitations}
              loading={loadingInvitations}
              organizationNames={organizationNames}
              invitationError={invitationError}
              processingInvitation={processingInvitation}
              onAccept={onAcceptInvitation}
              getDaysUntilExpiration={getDaysUntilExpiration}
              isInvitationExpired={isInvitationExpired}
            />
          ) : (
            // No invitations - show available orgs or create button
            <Stack spacing={3}>
              {/* Available Organizations (with verified domains) */}
              {availableOrganizations.length > 0 && (
                <Box>
                  <Typography level="title-md" sx={{ mb: 2, fontWeight: 600 }}>
                    Available Organizations
                  </Typography>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    Your email domain matches verified domains for these
                    organizations. You can request access to join.
                  </Typography>
                  <Stack spacing={2}>
                    {availableOrganizations.map((org) => (
                      <Card key={org.organization_id} variant="outlined">
                        <CardContent>
                          <Stack spacing={2}>
                            <Box>
                              <Typography
                                level="title-md"
                                sx={{ fontWeight: 600 }}
                              >
                                {org.organization_name}
                              </Typography>
                              <Typography
                                level="body-xs"
                                color="neutral"
                                sx={{ mt: 0.5 }}
                              >
                                Verified domains:{" "}
                                {org.verified_domains?.join(", ") || "None"}
                              </Typography>
                            </Box>
                            <Button
                              variant="solid"
                              color="primary"
                              size="sm"
                              onClick={() => onOpenRequestAccessModal(org)}
                            >
                              Request Access
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Create Organization Option - Only show if no matching domains */}
              {availableOrganizations.length === 0 && (
                <Box>
                  <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    You do not currently belong to an organization.
                  </Typography>
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Button
                      variant="solid"
                      color="primary"
                      onClick={onCreateOrganization}
                      sx={{ minWidth: "200px" }}
                    >
                      Create Organization
                    </Button>
                  </Box>
                </Box>
              )}
            </Stack>
          )
        ) : // Has organization - show org details
        loadingOrganizationDetails ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Loading />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                  Organization Name
                </Typography>
                <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                  {organizationName || "Unknown Organization"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                  Your Role
                </Typography>
                <Typography level="title-md" sx={{ fontWeight: 500 }}>
                  {isAdmin ? "Administrator" : "Member"}
                </Typography>
              </Box>
            </Stack>

            {/* Organization Status - Admin Only */}
            {orgStatus && isAdmin && (
              <Box>
                <Typography level="body-sm" color="neutral" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <Chip
                  size="sm"
                  variant="soft"
                  color={
                    orgStatus === "active"
                      ? "success"
                      : orgStatus === "pending_payment"
                        ? "warning"
                        : orgStatus === "pending_setup"
                          ? "neutral"
                          : "danger"
                  }
                >
                  {orgStatus === "active"
                    ? "Active"
                    : orgStatus === "pending_payment"
                      ? "Pending Payment"
                      : orgStatus === "pending_setup"
                        ? "Setup Required"
                        : "Suspended"}
                </Chip>
              </Box>
            )}

            {/* Next Step Actions - Admin Only */}
            {isAdmin &&
            (orgStatus === "pending_setup" ||
              orgStatus === "pending_payment") ? (
              <>
                <Divider />
                <Alert color="warning" variant="soft">
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
                    Next Step: Complete Billing Setup
                  </Typography>
                  <Typography level="body-sm" sx={{ mb: 2 }}>
                    Choose a billing package to activate your organization and
                    start inviting users.
                  </Typography>
                  <Button
                    variant="solid"
                    color="primary"
                    component={Link}
                    to="/subscription/select-package"
                    size="sm"
                  >
                    Go to Billing
                  </Button>
                </Alert>
              </>
            ) : null}

            {/* Organization Settings Link for Admins */}
            {isAdmin && (
              <>
                <Divider />
                <Button
                  variant="outlined"
                  color="neutral"
                  component={Link}
                  to="/organization/settings"
                  startDecorator={<SettingsIcon />}
                  fullWidth
                >
                  Manage Organization
                </Button>
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationCard;
